import Seo from "../components/Seo";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { API } from "../lib/site";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";
import NewsletterForm from "../components/NewsletterForm";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Message received. We will be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Sorry, your message could not be sent. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-navy-100 bg-white px-5 py-3.5 text-base text-navy placeholder:text-slate-400 outline-none transition-shadow duration-200 focus-visible:ring-2 focus-visible:ring-navy";

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact & Enrol | Telopea Park OSHC"
        description="Contact Telopea Park OSHC in Barton, Canberra. Address, phone, email and enrolment/waitlist information for our not-for-profit OSHC."
        path="/contact"
      />
      <PageHeader
        testId="contact-header"
        eyebrow="Contact"
        title="We'd love to hear from you"
        description="Questions about enrolment, fees, programs or anything else - send us a message and our team will get back to you."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24" data-testid="contact-content">
        <div className="grid gap-12 lg:grid-cols-5">
          <FadeIn className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="contact-visit-card">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
                <MapPin className="h-6 w-6" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-semibold text-navy">Visit us</h2>
              <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed">
                Telopea Park OSHC
                <br />
                On the grounds of Telopea Park School
                <br />
                New South Wales Crescent
                <br />
                Barton ACT 2600
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="contact-phone-card">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
                <Phone className="h-6 w-6" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-semibold text-navy">Call us</h2>
              <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed">
                <a
                  href="tel:+61426692553"
                  data-testid="contact-phone-link"
                  className="font-semibold text-navy underline decoration-waratah decoration-2 underline-offset-4 transition-colors duration-200 hover:text-waratah"
                >
                  0426 692 553
                </a>
                <br />
                Work phone - 8:00am to 6:00pm
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="contact-email-card">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
                <Mail className="h-6 w-6" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-semibold text-navy">Email us</h2>
              <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed">
                <a
                  href="mailto:admin@tpsoshc.org.au"
                  data-testid="contact-email-link"
                  className="font-semibold text-navy underline decoration-waratah decoration-2 underline-offset-4 transition-colors duration-200 hover:text-waratah break-all"
                >
                  admin@tpsoshc.org.au
                </a>
                <br />
                For enrolment and admin enquiries
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="contact-hours-card">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
                <Clock className="h-6 w-6" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-semibold text-navy">Session times</h2>
              <ul className="mt-3 space-y-2 text-base sm:text-lg text-slate-600">
                <li>Before School Care: 7:30am - 9:00am</li>
                <li>After School Care: 3:15pm - 6:00pm</li>
                <li>Vacation Care: 8:00am - 6:00pm</li>
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} className="lg:col-span-3">
            <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-surface p-8 sm:p-10 shadow-sm" data-testid="contact-form">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-navy">Send a message</h2>
              <div className="mt-6 space-y-5">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-sm font-semibold text-navy">
                    Your name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Citizen"
                    data-testid="contact-name-input"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-sm font-semibold text-navy">
                    Email address
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    data-testid="contact-email-input"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="mb-2 block text-sm font-semibold text-navy">
                    Subject
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    required
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="e.g. Enrolment enquiry"
                    data-testid="contact-subject-input"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-2 block text-sm font-semibold text-navy">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help?"
                    data-testid="contact-message-input"
                    className={`${inputClass} resize-y`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 rounded-full bg-waratah px-8 py-4 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-waratah-600 hover:shadow-md disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                >
                  <Send className="h-4 w-4" /> {loading ? "Sending..." : "Send message"}
                </button>
              </div>
            </form>
          </FadeIn>
        </div>
      </section>

      <section className="bg-surface border-t border-navy-100" data-testid="contact-newsletter">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-16 text-center">
          <FadeIn>
            <h2 className="font-display text-3xl font-semibold text-navy">Prefer updates by email?</h2>
            <p className="mx-auto mt-3 max-w-xl text-lg text-slate-600">
              Subscribe for occasional updates about programs, vacation care dates and enrolment news.
            </p>
            <div className="mx-auto mt-7 max-w-lg">
              <NewsletterForm />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Contact;
